import { useEffect, useMemo, useRef, useState } from "react";
import { Download, FileSpreadsheet, Filter as FilterIcon, RotateCcw } from "lucide-react";

import { Card } from "../../../../shared/card/card.tsx";
import {
  Button,
  DateRange,
  Select,
  type SelectOption,
} from "../../../../ui/index.ts";
import { formatToDateOnly } from "../../../../../../utils/helpers.js";
import { useEmployeeNamesListStore } from "../../../../../../stores/employeeNamesListStore.js";
import {
  DEFAULT_STATUS_FILTER,
  REGION_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from "../../constants.ts";
import type {
  ApprovalDashboardFilters as ApprovalDashboardFilterValues,
  ApprovalRegion,
  ApprovalStatusFilter,
} from "../../types.ts";

import styles from "./approval-dashboard-filters.module.css";

export interface ApprovalDashboardFiltersProps {
  value: ApprovalDashboardFilterValues;
  onApply: (filters: ApprovalDashboardFilterValues) => void;
  onReset: () => void;
  onExport: (
    report: "approval" | "filling" | "projectTime",
    filters: ApprovalDashboardFilterValues,
  ) => void;
  exporting?: boolean;
  loading?: boolean;
}

interface EmployeeOption {
  value: string | number;
  label: string;
}

function toSelectValue(id: number | null | undefined): string {
  return id == null ? "" : String(id);
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function ApprovalDashboardFilters({
  value,
  onApply,
  onReset,
  onExport,
  exporting,
  loading,
}: ApprovalDashboardFiltersProps) {
  const { employeeList, fetchEmployees } = useEmployeeNamesListStore();

  const [approverId, setApproverId] = useState<string>(
    toSelectValue(value.approverId),
  );
  const [employeeId, setEmployeeId] = useState<string>(
    toSelectValue(value.employeeId),
  );
  const [region, setRegion] = useState<string>(value.region ?? "");
  const [status, setStatus] = useState<ApprovalStatusFilter>(
    value.status ?? DEFAULT_STATUS_FILTER,
  );
  const [startDate, setStartDate] = useState<Date | null>(
    parseDate(value.fromDate),
  );
  const [endDate, setEndDate] = useState<Date | null>(parseDate(value.toDate));
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!exportMenuOpen) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!exportMenuRef.current?.contains(event.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExportMenuOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [exportMenuOpen]);

  useEffect(() => {
    if (employeeList.length === 0) {
      void fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const peopleOptions: SelectOption[] = useMemo(
    () =>
      (employeeList as EmployeeOption[]).map((o) => ({
        value: String(o.value),
        label: o.label,
      })),
    [employeeList],
  );

  const statusOptions: SelectOption[] = useMemo(
    () =>
      STATUS_FILTER_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    [],
  );

  const regionOptions: SelectOption[] = useMemo(
    () => REGION_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
    [],
  );

  const buildFilters = (): ApprovalDashboardFilterValues => ({
    approverId: approverId ? Number(approverId) : null,
    employeeId: employeeId ? Number(employeeId) : null,
    region: (region as ApprovalRegion) || null,
    status,
    fromDate: startDate ? formatToDateOnly(startDate) : null,
    toDate: endDate ? formatToDateOnly(endDate) : null,
  });

  const handleApply = () => onApply(buildFilters());

  const handleReset = () => {
    setApproverId("");
    setEmployeeId("");
    setRegion("");
    setStatus(DEFAULT_STATUS_FILTER);
    setStartDate(null);
    setEndDate(null);
    onReset();
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start || null);
    setEndDate(end || null);
  };

  return (
    <Card className={styles.card}>
      <div className={styles.row}>
        <div className={styles.field}>
          <Select
            label="Approver"
            placeholder="Select Approver"
            options={peopleOptions}
            value={approverId}
            onChange={setApproverId}
            isClearable
            searchable
          />
        </div>

        <div className={styles.field}>
          <Select
            label="Employee"
            placeholder="Select Employee"
            options={peopleOptions}
            value={employeeId}
            onChange={setEmployeeId}
            isClearable
            searchable
          />
        </div>

        <div className={styles.field}>
          <Select
            label="Region"
            placeholder="Choose"
            options={regionOptions}
            value={region}
            onChange={setRegion}
            isClearable
          />
        </div>

        <div className={styles.field}>
          <Select
            label="Status"
            placeholder="Select Status"
            options={statusOptions}
            value={status}
            onChange={(v) =>
              setStatus((v as ApprovalStatusFilter) || DEFAULT_STATUS_FILTER)
            }
          />
        </div>

        <div className={styles.fieldDate}>
          <DateRange
            label="Date"
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            placeholderText="Select Date Range"
          />
        </div>

        <div className={styles.actions}>
          <Button
            variant="accent"
            type="button"
            onClick={handleApply}
            disabled={loading}
            startIcon={<FilterIcon size={14} aria-hidden />}
          >
            Apply Filter
          </Button>
          <Button
            variant="light-grey"
            type="button"
            onClick={handleReset}
            disabled={loading}
            startIcon={<RotateCcw size={14} aria-hidden />}
          >
            Clear Filter
          </Button>
          <div className={styles.exportMenu} ref={exportMenuRef}>
          <Button
            variant="accent"
            type="button"
            onClick={() => setExportMenuOpen((open) => !open)}
            disabled={exporting || loading}
            startIcon={<Download size={14} aria-hidden />}
            aria-haspopup="menu"
            aria-expanded={exportMenuOpen}
          >
            {exporting ? "Exporting…" : "Export"}
          </Button>
          {exportMenuOpen ? (
            <div className={styles.exportOptions} role="menu">
              <button type="button" role="menuitem" onClick={() => {
                setExportMenuOpen(false);
                onExport("approval", buildFilters());
              }}>
                <FileSpreadsheet size={16} aria-hidden />
                <span>
                  <p className="body-sm">Employee Timesheet Report</p>
                </span>
              </button>
              <button type="button" role="menuitem" onClick={() => {
                setExportMenuOpen(false);
                onExport("filling", buildFilters());
              }}>
                <FileSpreadsheet size={16} aria-hidden />
                <span>
                  <p className="body-sm">Timesheet Submission Report</p>
                </span>
              </button>
              <button type="button" role="menuitem" onClick={() => {
                setExportMenuOpen(false);
                onExport("projectTime", buildFilters());
              }}>
                <FileSpreadsheet size={16} aria-hidden />
                <span>
                  <p className="body-sm"> Employee Project Hours Report</p>
                </span>
              </button>
            </div>
          ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default ApprovalDashboardFilters;
